import React, { useState, useMemo } from 'react';
import CommonCard from '../../../global/card';
import CommonSkeletonLoader from '../../../global/loading';
import { useLocation } from 'react-router-dom';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import useDebounce from '../../../../utils/customHooks';
import CommonPagination from '../../../global/pagination';
import EnhancedNewLeadAlertList from './EnhancedNewLeadAlertList';

const EnhancedNewLeadAlert = () => {
    const { DEFAULT_RECORDS_LISTING, DEFAULT_RECORDS_SEARCH } = SOURCES;

    const [searchNewLeadAlert, setSearchNewLeadAlert] = useState("");
    const [newLeadAlertViewType, setNewLeadAlertViewType] = useState(() => {
        return localStorage.getItem("newLeadAlertViewType") || "GRID";
    });
    // Default sort as per old component: sortBy="", sortOrder=1
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });

    // Pagination
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(searchParams.get("limit") || DEFAULT_RECORDS_LISTING, 10);
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [paginationSearch, setPaginationSearch] = useState({
        offsetSearch: 0,
        limitSearch: DEFAULT_RECORDS_SEARCH,
    });

    const debouncedSearchTerm = useDebounce(searchNewLeadAlert, 500);

    // Params for main listing
    const keyParamsForNewLeadAlert = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Params for search
    const searchKeyParams = {
        q: debouncedSearchTerm,
    };

    // Main Data Query
    const { data: newLeadAlertData, isLoading: newLeadAlertLoading, isFetching: newLeadAlertFetching } = useGenericQuery({
        endpoint: "alert/alert-logs",
        queryKey: QUERY_KEYS.newLeadAlert.lists({ ...keyParamsForNewLeadAlert }),
        queryParams: { ...keyParamsForNewLeadAlert },
        enabled: !debouncedSearchTerm
    });

    // Search Data Query
    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "alert/alert-logs-search",
        queryKey: QUERY_KEYS.newLeadAlert.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        enabled: !!debouncedSearchTerm.trim()
    });

    // Determine final data
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm) {
            return searchData?.results || [];
        }
        return newLeadAlertData?.results || [];
    }, [newLeadAlertData, searchData, debouncedSearchTerm]);

    const totalRecords = debouncedSearchTerm ? (searchData?.totalRecords || 0) : (newLeadAlertData?.totalRecords || 0);
    const isLoading = newLeadAlertLoading || searchDataLoading;
    const dataExist = getFinalData && getFinalData.length > 0;

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search lead alerts...",
                searchInput: searchNewLeadAlert,
                setSearchInput: setSearchNewLeadAlert
            }}
            isListGridView={{
                show: true,
                viewType: newLeadAlertViewType,
                setViewType: setNewLeadAlertViewType,
                viewFor: "newLeadAlertViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={newLeadAlertViewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            className={(!isLoading && newLeadAlertFetching) ? "opacity-50" : ""}
        >
            <EnhancedNewLeadAlertList
                isLoading={isLoading}
                newLeadAlertData={getFinalData}
                sort={sort}
                setSort={setSort}
                viewType={newLeadAlertViewType}
            />

            {(dataExist && !searchNewLeadAlert) &&
                totalRecords > DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="New Lead Alert"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    );
};

export default EnhancedNewLeadAlert;